import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Image,
  Badge,
  Button,
  Modal,
  ProgressBar,
  Tab,
  Tabs,
  Form,
  InputGroup
} from 'react-bootstrap';
import { 
  FaBuilding, FaHammer, FaLaptopCode, FaServer,
  FaIndustry, FaUserTie, FaRoad, FaCube,
  FaCalendarAlt, FaMapMarkerAlt, FaUsers, FaMoneyBill,
  FaChartLine, FaStar, FaEye, FaSearch, FaFilter,
  FaArrowRight, FaPhone, FaEnvelope, FaUser,
  FaClock, FaCheckCircle, FaPauseCircle, FaTimesCircle,
  FaCalendarCheck, FaShareAlt, FaDownload, FaExpand,
  FaArrowLeft, FaArrowRight as FaArrowRightIcon
} from 'react-icons/fa';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    ongoing: 0,
    completed: 0,
    budget: 0
  });

  const projectTypes = [
    'All', 'Real Estate', 'Construction', 'Software Development',
    'IT Services', 'Manufacturing', 'Consulting', 'Infrastructure', 'Other'
  ];

  const projectStatuses = [
    'All', 'Planned', 'Ongoing', 'On Hold', 'Completed', 'Cancelled'
  ];

  useEffect(() => {
    fetchProjects();
    fetchFeaturedProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${BASE_URL}/projects.php`);
      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        const projectsData = result.data.map(project => ({
          ...project,
          id: parseInt(project.id),
          budget: parseFloat(project.budget) || 0,
          progress_percentage: parseInt(project.progress_percentage) || 0,
          team_size: parseInt(project.team_size) || 0
        }));
        
        setProjects(projectsData);
        
        // Calculate statistics
        const ongoing = projectsData.filter(p => p.project_status === 'Ongoing').length;
        const completed = projectsData.filter(p => p.project_status === 'Completed').length;
        const totalBudget = projectsData.reduce((sum, p) => sum + (p.budget || 0), 0);
        
        setStats({
          total: projectsData.length,
          ongoing,
          completed,
          budget: totalBudget
        });
      } else {
        setError('No projects available');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedProjects = async () => {
    try {
      const response = await fetch(`${BASE_URL}/projects.php?featured=true&limit=3`);
      const result = await response.json();
      
      if (result.success && Array.isArray(result.data)) {
        setFeaturedProjects(result.data);
      }
    } catch (err) {
      console.error('Error fetching featured projects:', err);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  const getProjectIcon = (projectType) => {
    const icons = {
      'Real Estate': <FaBuilding />,
      'Construction': <FaHammer />,
      'Software Development': <FaLaptopCode />,
      'IT Services': <FaServer />,
      'Manufacturing': <FaIndustry />,
      'Consulting': <FaUserTie />,
      'Infrastructure': <FaRoad />,
      'Other': <FaCube />
    };
    return icons[projectType] || <FaCube />;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'Planned': 'info',
      'Ongoing': 'primary',
      'On Hold': 'warning',
      'Completed': 'success',
      'Cancelled': 'danger'
    };
    return badges[status] || 'secondary';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Planned': <FaCalendarAlt className="me-1" />,
      'Ongoing': <FaClock className="me-1" />,
      'On Hold': <FaPauseCircle className="me-1" />,
      'Completed': <FaCheckCircle className="me-1" />,
      'Cancelled': <FaTimesCircle className="me-1" />
    };
    return icons[status] || <FaCalendarAlt className="me-1" />;
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Fixed date formatting function - removed date-fns dependency
  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === '0000-00-00 00:00:00' || dateString === 'null') {
      return 'N/A';
    }
    
    try {
      let date;
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-');
        if (year === '0000') {
          return 'N/A';
        }
        date = new Date(year, month - 1, day);
      } else {
        return 'Invalid Date';
      }
      
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error, 'Input:', dateString);
      return 'Invalid Date';
    }
  };

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedProject(null);
    setShowModal(false);
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.short_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = activeFilter === 'all' || project.project_type === activeFilter;
    
    return matchesSearch && matchesType;
  });

  const getFilteredProjectsByTab = () => {
    if (activeTab === 'all') return filteredProjects;
    if (activeTab === 'featured') return filteredProjects.filter(p => p.is_featured);
    if (activeTab === 'ongoing') return filteredProjects.filter(p => p.project_status === 'Ongoing');
    if (activeTab === 'completed') return filteredProjects.filter(p => p.project_status === 'Completed');
    return filteredProjects;
  };

  if (loading) {
    return (
      <Container className="projects-container py-5">
        <div className="text-center">
          <div className="loading-animation">
            <div className="loading-dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
          <h4 className="mt-4 text-gradient">Loading Projects</h4>
          <p className="text-muted">Discovering our portfolio...</p>
        </div>
      </Container>
    );
  }

  if (error || projects.length === 0) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="border-0 shadow-sm">
          <Alert.Heading>Projects Not Available</Alert.Heading>
          <p>{error}</p>
          <Button variant="primary" onClick={fetchProjects}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="projects-portal">
      {/* Hero Section */}
      <div className="projects-hero">
        <Container className="py-5">
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <Badge bg="primary" className="hero-badge mb-3">
                <FaStar className="me-2" /> Our Portfolio
              </Badge>
              <h1 className="display-5 fw-bold text-white mb-3">
                Our <span className="text-gradient">Projects</span> Portfolio
              </h1>
              <p className="lead text-white-50 mb-4">
                Explore our diverse portfolio of successful projects across various industries.
                Each project represents our commitment to excellence and innovation.
              </p>
              
              <div className="hero-stats">
                <div className="stat-item">
                  <h3 className="text-white mb-0">{stats.total}</h3>
                  <small className="text-white-50">Total Projects</small>
                </div>
                <div className="stat-item">
                  <h3 className="text-white mb-0">{stats.ongoing}</h3>
                  <small className="text-white-50">Ongoing</small>
                </div>
                <div className="stat-item">
                  <h3 className="text-white mb-0">{stats.completed}</h3>
                  <small className="text-white-50">Completed</small>
                </div>
                <div className="stat-item">
                  <h3 className="text-white mb-0">{formatCurrency(stats.budget)}</h3>
                  <small className="text-white-50">Total Budget</small>
                </div>
              </div>
            </Col>
            
            <Col lg={6}>
              <div className="hero-visual">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <div className="floating-shape shape-3"></div>
                
                {featuredProjects.length > 0 && (
                  <div className="featured-preview">
                    {featuredProjects.map((project, index) => (
                      <div 
                        key={project.id} 
                        className={`preview-card preview-${index + 1}`}
                        style={{ 
                          background: `linear-gradient(135deg, ${getGradientColor(index)})`
                        }}
                      >
                        <div className="preview-icon">
                          {getProjectIcon(project.project_type)}
                        </div>
                        <h6 className="mb-1 text-white">{project.project_name}</h6>
                        <small className="text-white-50">{project.project_type}</small>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        {/* Search and Filter Section */}
        <div className="search-filter-section mb-5">
          <Row className="align-items-center">
            <Col md={6} lg={4}>
              <div className="search-box">
                <div className="search-icon">
                  <FaSearch />
                </div>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Button 
                    variant="link" 
                    className="search-clear"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </Col>
            
            <Col md={6} lg={8}>
              <div className="filters-scroll">
                <div className="filters-container">
                  {projectTypes.map(type => (
                    <Button
                      key={type}
                      variant={activeFilter === type ? 'primary' : 'outline-primary'}
                      className="filter-btn me-2 mb-2"
                      onClick={() => setActiveFilter(type)}
                    >
                      {type === 'All' ? (
                        <>
                          <FaFilter className="me-2" />
                          {type}
                        </>
                      ) : (
                        <>
                          {getProjectIcon(type)}
                          <span className="ms-2">{type}</span>
                        </>
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-4 custom-tabs"
        >
          <Tab eventKey="all" title={
            <>
              <FaCube className="me-2" />
              All Projects
              <Badge bg="secondary" className="ms-2">{filteredProjects.length}</Badge>
            </>
          }>
            {/* Project Grid */}
            <Row className="g-4">
              {getFilteredProjectsByTab().map((project, index) => (
                <Col lg={4} md={6} key={project.id}>
                  <div className="project-card-wrapper">
                    <Card className="project-card border-0 shadow-lg">
                      <div className="project-card-image">
                        {project.thumbnail_image ? (
                          <Card.Img 
                            variant="top" 
                            src={getImageUrl(project.thumbnail_image)}
                            alt={project.project_name}
                          />
                        ) : (
                          <div className="project-image-placeholder">
                            <div className="placeholder-icon">
                              {getProjectIcon(project.project_type)}
                            </div>
                          </div>
                        )}
                        
                        <div className="project-card-overlay">
                          <div className="overlay-badges">
                            <Badge bg={getStatusBadge(project.project_status)}>
                              {getStatusIcon(project.project_status)}
                              {project.project_status}
                            </Badge>
                            {project.is_featured && (
                              <Badge bg="warning" text="dark">
                                <FaStar className="me-1" />
                                Featured
                              </Badge>
                            )}
                          </div>
                          
                          <div className="overlay-actions">
                            <Button
                              variant="light"
                              size="sm"
                              className="action-btn"
                              onClick={() => handleViewDetails(project)}
                            >
                              <FaEye className="me-1" />
                              View Details
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      <Card.Body className="p-4">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h4 className="project-title mb-1">{project.project_name}</h4>
                            <Card.Subtitle className="text-muted">
                              {getProjectIcon(project.project_type)}
                              <span className="ms-2">{project.project_type}</span>
                            </Card.Subtitle>
                          </div>
                          <Badge bg="light" text="dark">
                            {project.project_code}
                          </Badge>
                        </div>
                        
                        <p className="project-description text-muted mb-4">
                          {project.short_description || 'No description available'}
                        </p>
                        
                        <div className="project-progress mb-4">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <small className="text-muted">Progress</small>
                            <small className="fw-bold">{project.progress_percentage}%</small>
                          </div>
                          <ProgressBar 
                            now={project.progress_percentage} 
                            variant={
                              project.progress_percentage >= 100 ? 'success' :
                              project.progress_percentage >= 50 ? 'primary' :
                              project.progress_percentage >= 25 ? 'warning' : 'info'
                            }
                            className="progress-sm"
                          />
                        </div>
                        
                        <div className="project-meta">
                          <Row className="g-2">
                            <Col xs={6}>
                              <div className="meta-item">
                                <FaMapMarkerAlt className="meta-icon" />
                                <div>
                                  <small className="text-muted">Location</small>
                                  <div className="meta-value">{project.location || 'N/A'}</div>
                                </div>
                              </div>
                            </Col>
                            <Col xs={6}>
                              <div className="meta-item">
                                <FaCalendarAlt className="meta-icon" />
                                <div>
                                  <small className="text-muted">Start Date</small>
                                  <div className="meta-value">{formatDate(project.start_date)}</div>
                                </div>
                              </div>
                            </Col>
                            <Col xs={6}>
                              <div className="meta-item">
                                <FaUsers className="meta-icon" />
                                <div>
                                  <small className="text-muted">Team Size</small>
                                  <div className="meta-value">{project.team_size || 0} members</div>
                                </div>
                              </div>
                            </Col>
                            <Col xs={6}>
                              <div className="meta-item">
                                <FaMoneyBill className="meta-icon" />
                                <div>
                                  <small className="text-muted">Budget</small>
                                  <div className="meta-value">
                                    {formatCurrency(project.budget, project.currency)}
                                  </div>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                </Col>
              ))}
            </Row>
          </Tab>
          
          <Tab eventKey="featured" title={
            <>
              <FaStar className="me-2" />
              Featured
              <Badge bg="warning" className="ms-2">
                {filteredProjects.filter(p => p.is_featured).length}
              </Badge>
            </>
          }>
            {/* Featured projects will be shown here */}
          </Tab>
          
          <Tab eventKey="ongoing" title={
            <>
              <FaClock className="me-2" />
              Ongoing
              <Badge bg="primary" className="ms-2">
                {filteredProjects.filter(p => p.project_status === 'Ongoing').length}
              </Badge>
            </>
          }>
            {/* Ongoing projects will be shown here */}
          </Tab>
          
          <Tab eventKey="completed" title={
            <>
              <FaCheckCircle className="me-2" />
              Completed
              <Badge bg="success" className="ms-2">
                {filteredProjects.filter(p => p.project_status === 'Completed').length}
              </Badge>
            </>
          }>
            {/* Completed projects will be shown here */}
          </Tab>
        </Tabs>

        {/* No Results Message */}
        {getFilteredProjectsByTab().length === 0 && (
          <div className="no-results text-center py-5">
            <div className="no-results-icon mb-4">
              <FaSearch size={60} className="text-muted" />
            </div>
            <h4 className="mb-3">No Projects Found</h4>
            <p className="text-muted mb-4">Try adjusting your search or filters</p>
            <Button 
              variant="outline-primary" 
              onClick={() => {
                setSearchTerm('');
                setActiveFilter('all');
                setActiveTab('all');
              }}
            >
              Show All Projects
            </Button>
          </div>
        )}
      </Container>

      {/* Project Detail Modal */}
      {selectedProject && (
        <Modal 
          show={showModal} 
          onHide={handleCloseModal} 
          centered 
          size="xl"
          className="project-detail-modal"
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>
              <div className="d-flex align-items-center">
                <div 
                  className="modal-project-icon me-3"
                  style={{ 
                    background: `linear-gradient(135deg, ${getGradientColor(0)})`
                  }}
                >
                  {getProjectIcon(selectedProject.project_type)}
                </div>
                <div>
                  <h3 className="mb-0">{selectedProject.project_name}</h3>
                  <div className="d-flex align-items-center mt-1">
                    <Badge bg="light" text="dark" className="me-2">
                      {selectedProject.project_code}
                    </Badge>
                    <Badge bg={getStatusBadge(selectedProject.project_status)}>
                      {getStatusIcon(selectedProject.project_status)}
                      {selectedProject.project_status}
                    </Badge>
                    {selectedProject.is_featured && (
                      <Badge bg="warning" text="dark" className="ms-2">
                        <FaStar className="me-1" />
                        Featured
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body className="pt-4">
            {/* Image Gallery */}
            {(selectedProject.gallery_images && selectedProject.gallery_images.length > 0) || 
             selectedProject.thumbnail_image ? (
              <div className="project-gallery mb-4">
                <Row className="g-2">
                  <Col lg={8}>
                    {selectedProject.gallery_images && selectedProject.gallery_images.length > 0 ? (
                      <div className="main-image">
                        <Image
                          src={getImageUrl(selectedProject.gallery_images[0])}
                          alt={selectedProject.project_name}
                          fluid
                          className="rounded"
                          style={{ maxHeight: '400px', objectFit: 'cover', width: '100%' }}
                        />
                      </div>
                    ) : (
                      <div className="main-image">
                        <Image
                          src={getImageUrl(selectedProject.thumbnail_image)}
                          alt={selectedProject.project_name}
                          fluid
                          className="rounded"
                          style={{ maxHeight: '400px', objectFit: 'cover', width: '100%' }}
                        />
                      </div>
                    )}
                  </Col>
                  
                  <Col lg={4}>
                    <Row className="g-2">
                      {selectedProject.gallery_images && selectedProject.gallery_images.slice(1, 5).map((image, index) => (
                        <Col xs={6} key={index}>
                          <div className="gallery-thumb">
                            <Image
                              src={getImageUrl(image)}
                              alt={`${selectedProject.project_name} - ${index + 2}`}
                              fluid
                              className="rounded"
                              style={{ height: '95px', objectFit: 'cover', width: '100%' }}
                            />
                          </div>
                        </Col>
                      ))}
                      {(!selectedProject.gallery_images || selectedProject.gallery_images.length === 1) && 
                       selectedProject.thumbnail_image && (
                        <Col xs={6}>
                          <div className="gallery-thumb">
                            <Image
                              src={getImageUrl(selectedProject.thumbnail_image)}
                              alt={selectedProject.project_name}
                              fluid
                              className="rounded"
                              style={{ height: '95px', objectFit: 'cover', width: '100%' }}
                            />
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </div>
            ) : (
              <div className="no-images mb-4 text-center py-5 bg-light rounded">
                <FaCube size={48} className="text-muted mb-3" />
                <p className="text-muted">No images available for this project</p>
              </div>
            )}
            
            <Row>
              <Col lg={8}>
                {/* Detailed Description */}
                <div className="project-description-section mb-4">
                  <h5 className="mb-3">Project Overview</h5>
                  <div className="project-full-description">
                    {selectedProject.detailed_description ? (
                      selectedProject.detailed_description.split('\n\n').map((paragraph, index) => (
                        <p key={index} className="mb-3">
                          {paragraph}
                        </p>
                      ))
                    ) : (
                      <p className="text-muted">
                        {selectedProject.short_description || 'No detailed description available.'}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Technologies & Materials */}
                <Row className="mb-4">
                  <Col md={6}>
                    <div className="technologies-section">
                      <h6 className="mb-3">Technologies Used</h6>
                      {selectedProject.technologies_used && selectedProject.technologies_used.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {selectedProject.technologies_used.map((tech, index) => (
                            <Badge key={index} bg="info" className="p-2">
                              {tech}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted small">No technologies specified</p>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="materials-section">
                      <h6 className="mb-3">Materials Used</h6>
                      {selectedProject.materials_used && selectedProject.materials_used.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {selectedProject.materials_used.map((material, index) => (
                            <Badge key={index} bg="secondary" className="p-2">
                              {material}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted small">No materials specified</p>
                      )}
                    </div>
                  </Col>
                </Row>
              </Col>
              
              <Col lg={4}>
                {/* Project Information Sidebar */}
                <div className="project-info-sidebar">
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-primary text-white">
                      <h6 className="mb-0">Project Information</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="info-item mb-3">
                        <div className="info-label text-muted">Progress</div>
                        <div className="info-value">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span>{selectedProject.progress_percentage}%</span>
                          </div>
                          <ProgressBar 
                            now={selectedProject.progress_percentage} 
                            variant={
                              selectedProject.progress_percentage >= 100 ? 'success' :
                              selectedProject.progress_percentage >= 50 ? 'primary' :
                              selectedProject.progress_percentage >= 25 ? 'warning' : 'info'
                            }
                            style={{ height: '8px' }}
                          />
                        </div>
                      </div>
                      
                      <div className="info-item mb-3">
                        <div className="info-label text-muted">Project Type</div>
                        <div className="info-value">
                          {getProjectIcon(selectedProject.project_type)}
                          <span className="ms-2">{selectedProject.project_type}</span>
                        </div>
                      </div>
                      
                      <div className="info-item mb-3">
                        <div className="info-label text-muted">Location</div>
                        <div className="info-value">
                          <FaMapMarkerAlt className="me-2 text-muted" />
                          {selectedProject.location || 'N/A'}
                        </div>
                      </div>
                      
                      <div className="info-item mb-3">
                        <div className="info-label text-muted">Start Date</div>
                        <div className="info-value">
                          <FaCalendarAlt className="me-2 text-muted" />
                          {formatDate(selectedProject.start_date)}
                        </div>
                      </div>
                      
                      <div className="info-item mb-3">
                        <div className="info-label text-muted">End Date</div>
                        <div className="info-value">
                          <FaCalendarCheck className="me-2 text-muted" />
                          {formatDate(selectedProject.end_date)}
                        </div>
                      </div>
                      
                      <div className="info-item mb-3">
                        <div className="info-label text-muted">Budget</div>
                        <div className="info-value fw-bold">
                          {formatCurrency(selectedProject.budget, selectedProject.currency)}
                        </div>
                      </div>
                      
                      <div className="info-item mb-3">
                        <div className="info-label text-muted">Team Size</div>
                        <div className="info-value">
                          <FaUsers className="me-2 text-muted" />
                          {selectedProject.team_size || 0} members
                        </div>
                      </div>
                      
                      <div className="info-item mb-3">
                        <div className="info-label text-muted">Project Manager</div>
                        <div className="info-value">
                          <FaUser className="me-2 text-muted" />
                          {selectedProject.project_manager || 'Not assigned'}
                        </div>
                      </div>
                      
                      {selectedProject.client_name && (
                        <div className="client-info mt-4 pt-3 border-top">
                          <h6 className="mb-3">Client Information</h6>
                          <div className="info-item mb-2">
                            <div className="info-label text-muted">Client Name</div>
                            <div className="info-value">
                              <FaUser className="me-2 text-muted" />
                              {selectedProject.client_name}
                            </div>
                          </div>
                          
                          {selectedProject.client_email && (
                            <div className="info-item mb-2">
                              <div className="info-label text-muted">Email</div>
                              <div className="info-value">
                                <FaEnvelope className="me-2 text-muted" />
                                {selectedProject.client_email}
                              </div>
                            </div>
                          )}
                          
                          {selectedProject.client_phone && (
                            <div className="info-item mb-2">
                              <div className="info-label text-muted">Phone</div>
                              <div className="info-value">
                                <FaPhone className="me-2 text-muted" />
                                {selectedProject.client_phone}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            </Row>
          </Modal.Body>
          
          <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={handleCloseModal}>
              Close
            </Button>
            <Button variant="primary">
              <FaEnvelope className="me-2" />
              Contact About This Project
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <style>{`
        .projects-portal {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          min-height: 100vh;
        }
        
        .projects-hero {
          background: linear-gradient(135deg, #2c3e50 0%, #4a6491 100%);
          position: relative;
          overflow: hidden;
        }
        
        .projects-hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E");
          opacity: 0.3;
        }
        
        .hero-badge {
          font-size: 0.9rem;
          padding: 8px 16px;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .text-gradient {
          background: linear-gradient(90deg, #667eea, #764ba2);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .hero-stats {
          display: flex;
          gap: 30px;
          margin-top: 30px;
        }
        
        .stat-item {
          display: flex;
          flex-direction: column;
        }
        
        .hero-visual {
          position: relative;
          height: 300px;
        }
        
        .floating-shape {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          animation: float 6s ease-in-out infinite;
        }
        
        .shape-1 {
          width: 100px;
          height: 100px;
          top: 20px;
          right: 50px;
          animation-delay: 0s;
        }
        
        .shape-2 {
          width: 70px;
          height: 70px;
          bottom: 40px;
          right: 100px;
          animation-delay: 2s;
        }
        
        .shape-3 {
          width: 50px;
          height: 50px;
          top: 100px;
          right: 150px;
          animation-delay: 4s;
        }
        
        .featured-preview {
          position: relative;
          height: 100%;
        }
        
        .preview-card {
          position: absolute;
          padding: 20px;
          border-radius: 15px;
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
          width: 200px;
          color: white;
        }
        
        .preview-1 {
          top: 20px;
          left: 0;
          z-index: 3;
        }
        
        .preview-2 {
          top: 100px;
          left: 100px;
          z-index: 2;
        }
        
        .preview-3 {
          top: 180px;
          left: 30px;
          z-index: 1;
        }
        
        .preview-icon {
          width: 50px;
          height: 50px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          margin-bottom: 15px;
          backdrop-filter: blur(10px);
        }
        
        .search-filter-section {
          background: white;
          padding: 25px;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
        }
        
        .search-box {
          position: relative;
        }
        
        .search-icon {
          position: absolute;
          left: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: #6c757d;
          z-index: 2;
        }
        
        .search-input {
          width: 100%;
          padding: 12px 20px 12px 45px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: white;
          padding-right: 100px;
        }
        
        .search-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 5px 15px rgba(102, 126, 234, 0.1);
        }
        
        .search-clear {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          padding: 5px 10px;
          font-size: 0.875rem;
          text-decoration: none;
        }
        
        .filters-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 10px;
        }
        
        .filters-container {
          display: flex;
          flex-wrap: nowrap;
          white-space: nowrap;
        }
        
        .filter-btn {
          display: inline-flex;
          align-items: center;
          white-space: nowrap;
        }
        
        .custom-tabs .nav-link {
          border: none;
          padding: 15px 25px;
          font-weight: 500;
          color: #6c757d;
          background: transparent;
          border-radius: 10px 10px 0 0;
          transition: all 0.3s ease;
        }
        
        .custom-tabs .nav-link.active {
          color: #667eea;
          background: white;
          box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
        }
        
        .project-card-wrapper {
          position: relative;
          height: 100%;
        }
        
        .project-card {
          border-radius: 15px;
          overflow: hidden;
          transition: all 0.4s ease;
          height: 100%;
        }
        
        .project-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
        }
        
        .project-card-image {
          position: relative;
          height: 200px;
          overflow: hidden;
        }
        
        .project-card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        
        .project-card:hover .project-card-image img {
          transform: scale(1.05);
        }
        
        .project-image-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .placeholder-icon {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          color: white;
          backdrop-filter: blur(10px);
        }
        
        .project-card-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.7));
          opacity: 0;
          transition: opacity 0.3s ease;
          padding: 15px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        
        .project-card:hover .project-card-overlay {
          opacity: 1;
        }
        
        .overlay-badges {
          display: flex;
          gap: 5px;
          flex-wrap: wrap;
        }
        
        .overlay-actions {
          text-align: center;
        }
        
        .action-btn {
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.3s ease;
        }
        
        .project-card:hover .action-btn {
          opacity: 1;
          transform: translateY(0);
        }
        
        .project-title {
          font-weight: 600;
          color: #333;
        }
        
        .project-description {
          line-height: 1.6;
          font-size: 0.95rem;
        }
        
        .progress-sm {
          height: 6px;
        }
        
        .project-meta {
          margin-top: 20px;
        }
        
        .meta-item {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .meta-icon {
          color: #667eea;
          font-size: 16px;
          min-width: 20px;
        }
        
        .meta-value {
          font-size: 0.9rem;
          font-weight: 500;
          line-height: 1.2;
        }
        
        .modal-project-icon {
          width: 60px;
          height: 60px;
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 28px;
        }
        
        .project-gallery .main-image {
          margin-bottom: 10px;
        }
        
        .gallery-thumb {
          cursor: pointer;
          transition: all 0.3s ease;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .gallery-thumb:hover {
          transform: scale(1.05);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
        }
        
        .project-info-sidebar .info-item {
          padding-bottom: 10px;
          border-bottom: 1px solid #f1f1f1;
        }
        
        .info-label {
          font-size: 0.8rem;
          margin-bottom: 4px;
        }
        
        .info-value {
          font-size: 0.95rem;
          display: flex;
          align-items: center;
        }
        
        .client-info {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 10px;
          margin-top: 20px;
        }
        
        .loading-animation {
          display: flex;
          justify-content: center;
        }
        
        .loading-dots {
          display: flex;
          gap: 10px;
        }
        
        .dot {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea, #764ba2);
          animation: bounce 1.4s infinite ease-in-out;
        }
        
        .dot:nth-child(1) { animation-delay: -0.32s; }
        .dot:nth-child(2) { animation-delay: -0.16s; }
        
        .no-results {
          background: white;
          border-radius: 15px;
          padding: 50px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
        
        @media (max-width: 768px) {
          .hero-stats {
            flex-direction: column;
            gap: 15px;
          }
          
          .filters-scroll {
            margin-top: 20px;
          }
          
          .featured-preview {
            margin-top: 40px;
            height: 200px;
          }
          
          .preview-card {
            width: 150px;
            padding: 15px;
          }
          
          .custom-tabs .nav-link {
            padding: 10px 15px;
            font-size: 0.9rem;
          }
        }
        
        @media (max-width: 576px) {
          .hero-visual {
            height: 200px;
          }
          
          .project-card-image {
            height: 180px;
          }
          
          .modal-project-icon {
            width: 50px;
            height: 50px;
            font-size: 24px;
          }
        }
      `}</style>
    </div>
  );
};

// Helper function for gradient colors
const getGradientColor = (index) => {
  const gradients = [
    '#667eea, #764ba2',
    '#f093fb, #f5576c',
    '#4facfe, #00f2fe',
    '#43e97b, #38f9d7',
    '#fa709a, #fee140',
    '#a8edea, #fed6e3',
    '#ff9a9e, #fad0c4',
    '#a1c4fd, #c2e9fb'
  ];
  return gradients[index % gradients.length];
};

export default ProjectsPage;